import { sessionItems } from "@/utils/sessionStorage";
import type {
  ApiService,
  ApiResponse,
  AssessmentResult,
  PageViewData,
  FriendlyCaptchaVerificationPayload,
  ResponsePayload,
  UtmsData,
  PollResultOutcome,
  PdfResponse,
  QuestionnaireResponse,
  SelfieUploadUrlResponse,
} from "./types";
import {
  CaptchaVerificationRequiredError,
  SessionRecordUnavailableError,
} from "./errors";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "";
const API_KEY = process.env.NEXT_PUBLIC_API_KEY || "";

const CAPTCHA_REQUIRED_FRAGMENT = "Friendly Captcha verification required";

function isCaptchaVerificationRequiredMessage(message: string): boolean {
  return message.includes(CAPTCHA_REQUIRED_FRAGMENT);
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? "");
}

function throwCaptchaVerificationRequiredError(): never {
  dispatchSessionExpiredEvent();
  throw new CaptchaVerificationRequiredError();
}

function throwIfCaptchaVerificationRequired(error: unknown): void {
  const message = getErrorMessage(error);
  if (isCaptchaVerificationRequiredMessage(message)) {
    throwCaptchaVerificationRequiredError();
  }
}

function dispatchSessionExpiredEvent(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("sessionKeyExpired", {
      detail: { key: sessionItems.SESSION_ID },
    }),
  );
}

/**
 * Generic fetch wrapper with error handling
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "x-api-key": API_KEY,
      ...options.headers,
    },
  });

  if (response.status === 410) {
    dispatchSessionExpiredEvent();
    throw new SessionRecordUnavailableError();
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => "Unknown error");
    const errorMessage =
      `API request failed: ${response.status} ${response.statusText} - ${errorBody}`;

    // Keep captcha enforcement behavior scoped to response submissions.
    if (
      endpoint === "/response" &&
      isCaptchaVerificationRequiredMessage(errorMessage)
    ) {
      throwCaptchaVerificationRequiredError();
    }

    throw new Error(errorMessage);
  }

  // Handle empty responses (e.g., 204 No Content)
  const contentType = response.headers.get("content-type");
  if (!contentType?.includes("application/json")) {
    return undefined as T;
  }

  return response.json();
}

/**
 * API service implementation - calls the actual API Gateway
 */
export const apiService: ApiService = {
  async getQuestions(questionnaireId: string): Promise<QuestionnaireResponse> {
    const response = await apiRequest<ApiResponse<QuestionnaireResponse>>(
      `/getQuestionnaire?id=${encodeURIComponent(questionnaireId)}`,
    );
    return response.data;
  },

  async checkForResult(
    sessionId: string,
    inferenceId: string,
  ): Promise<PollResultOutcome> {
    try {
      const body = await apiRequest<{ data?: { result?: AssessmentResult } }>(
        "/result",
        {
          method: "POST",
          body: JSON.stringify({
            id: sessionId,
            inference_id: inferenceId,
          }),
        },
      );
      if (body?.data?.result) {
        return { status: "ready", result: body.data.result };
      }
      return { status: "pending" };
    } catch {
      return { status: "error" };
    }
  },

  async postPageView(data: PageViewData): Promise<void> {
    await apiRequest<void>("/analytics", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  async postUtms(data: UtmsData): Promise<void> {
    await apiRequest<void>("/utms", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  async postResponse(data: ResponsePayload): Promise<string | null> {
    const response = await apiRequest<
      { data?: { inference_id?: string }; error?: string } | undefined
    >("/response", {
      method: "POST",
      body: JSON.stringify({
        id: data.id,
        survey_id: data.surveyId,
        survey_version: data.surveyVersion,
        survey_variant: data.surveyVariant,
        api_client: data.apiClient,
        ...(data.event && { event: data.event }),
        answers: data.answers,
        ...(data.experimentAssignments && {
          experiment_assignments: data.experimentAssignments,
        }),
        marketing_consent: data.marketingConsent,
      }),
    });

    // Response can be normalized to 200; treat logical captcha failures as terminal.
    if (response?.error) {
      throwIfCaptchaVerificationRequired(response.error);
      throw new Error(`API request failed: logical error - ${response.error}`);
    }

    return response?.data?.inference_id ?? null;
  },

  async verifyFriendlyCaptcha(
    data: FriendlyCaptchaVerificationPayload,
  ): Promise<boolean> {
    const response = await apiRequest<{ data?: { verified?: boolean } }>(
      "/captcha/verify",
      {
        method: "POST",
        body: JSON.stringify({
          id: data.id,
          friendly_captcha_solution: data.friendlyCaptchaSolution,
          ...(data.marketingConsent !== undefined && {
            marketing_consent: data.marketingConsent,
          }),
        }),
      },
    );
    return response?.data?.verified === true;
  },

  async generatePdf(
    sessionId: string,
    inferenceId: string,
    date: string,
  ): Promise<PdfResponse> {
    const response = await apiRequest<ApiResponse<PdfResponse>>("/pdf", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        inference_id: inferenceId,
        date: date
      }),
    });
    return response.data;
  },

  async getSelfieUploadUrl(
    sessionId: string,
  ): Promise<SelfieUploadUrlResponse> {
    const response = await apiRequest<ApiResponse<SelfieUploadUrlResponse>>(
      `/selfie-upload-url?session_id=${encodeURIComponent(sessionId)}`,
    );
    return response.data;
  },
};
